import { inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, NavigationEnd } from '@angular/router';
import { SelectedLocationType } from 'src/app/enums/selected-location-type.enum';
import { combineLatest, distinctUntilChanged, filter, map, Observable, of, shareReplay, startWith, switchMap } from 'rxjs';
import { Store } from '@ngxs/store';
import { CurrentAccountState } from 'src/states/current-account/current-account.state';
import { accessLevelBelongToGroup } from 'src/app/utils/access-level-belong-to-group.util';
import { SideBarMenuItemGroup } from 'src/app/models/side-bar-menu-item-group';
import { LoginClaimsState } from 'src/modules/security/states/login-claims/login-claims.state';
import { UserLevel } from 'src/modules/security/enums/user-level.enum';
import { CurrentLocationSettingsState } from 'src/states';
import { AppFeatures } from 'src/app/enums/app-features.enum';
import { AppRouteEnum } from 'src/app/enums/app-route.enum';
import { UserAccessLevelGroupName } from 'src/app/enums/user-access-level-group-name.enum';
import { menuItemGroupsMap } from 'src/app/components/side-bar/consts/menu-items-groups-map.const';
import { adminMenuItemsGroup } from 'src/app/components/side-bar/consts/admin-menu-items-group.const';
import { SideBarMenuItem } from 'src/app/models/side-bar-menu-item';
import { PscHomeLocationSettingsState } from 'src/states/psc-home-location-settings/psc-home-location-settings.state';
import { PreaccessioningService } from 'src/shared/services/preaccessioning.service';

@Injectable({ providedIn: 'root' })
export class SideBarService {
  private readonly store: Store = inject(Store);
  private readonly preaccessioningService = inject(PreaccessioningService);
  private readonly router: Router = inject(Router);
  private readonly adminUrlPrefix = `/${AppRouteEnum.PracticeAdmin}`;

  public readonly currentUrl$: Observable<string> = this.router.events.pipe(
    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    map(() => this.router.url),
    startWith(this.router.url),
    distinctUntilChanged(),
    // shareReplay keeps late subscribers in sync with the latest URL instead of the
    // value captured when this observable was constructed.
    shareReplay(1)
  );

  // Admin mode requires both the admin URL and the Practice Administrator access level, so a
  // non-admin who navigates directly to /practice-admin never sees admin sidebar UI before the
  // access guard redirects them to Forbidden.
  private readonly hasAdminAccess$: Observable<boolean> = this.store.select(LoginClaimsState.access).pipe(
    map(access => access !== null && accessLevelBelongToGroup(UserAccessLevelGroupName.AdminAccessLevelGroup, access)),
    distinctUntilChanged()
  );

  public readonly isAdminMode$: Observable<boolean> = combineLatest([this.currentUrl$, this.hasAdminAccess$]).pipe(
    map(([url, hasAdminAccess]) => hasAdminAccess && url.startsWith(this.adminUrlPrefix)),
    distinctUntilChanged()
  );

  // Tracks the last non-admin URL so exiting admin mode can return there. Derived as a
  // signal so the subscription is tied to the service lifecycle (no manual teardown).
  private readonly lastNonAdminUrl: Signal<string | null> = toSignal(
    this.currentUrl$.pipe(filter(url => !url.startsWith(this.adminUrlPrefix))),
    { initialValue: null }
  );

  public getReturnUrl(): string {
    return this.lastNonAdminUrl() ?? this.getDefaultReturnUrl();
  }

  private getDefaultReturnUrl(): string {
    const locationType = this.store.selectSnapshot(CurrentAccountState.type);
    return locationType === SelectedLocationType.PscHome ? `/${AppRouteEnum.PscSearch}` : `/${AppRouteEnum.Patients}`;
  }

  public readonly selectedLocationType$: Observable<SelectedLocationType | undefined> = this.store.select(CurrentAccountState.type);
  public readonly userAccessLevel$: Observable<UserLevel | null> = this.store.select(LoginClaimsState.access);
  public readonly isSupplyOrderingEnabled$: Observable<boolean | undefined> = this.store.select(
    CurrentLocationSettingsState.isSupplyOrderingEnabled
  );
  public readonly isGlobalResultsSearchEnabled$: Observable<boolean | undefined> = this.store.select(
    CurrentLocationSettingsState.isGlobalResultsSearchEnabled
  );
  public readonly isEmrEnabled$: Observable<boolean | undefined> = this.store.select(CurrentLocationSettingsState.isEmrEnabled);
  public readonly isPreAccessioningEnabled$: Observable<boolean> = this.preaccessioningService.isPreAccessioningEnabled$;
  public readonly isAnalyticsEnabled$: Observable<boolean> = this.store.select(LoginClaimsState.isAnalyticsEnabled);

  public readonly isSettingsLoading$ = combineLatest([
    this.store.select(PscHomeLocationSettingsState.loaded),
    this.store.select(CurrentAccountState.userLocation),
    this.store.select(CurrentLocationSettingsState.loaded)
  ]).pipe(
    map(([homeSettingsLoaded, userLocation, currentSettingsLoaded]) => {
      return userLocation ? !currentSettingsLoaded : !homeSettingsLoaded;
    })
  );
  private readonly locationMenuItemGroups$: Observable<SideBarMenuItemGroup[]> = combineLatest([
    this.selectedLocationType$,
    this.userAccessLevel$
  ]).pipe(
    filter((value): value is [SelectedLocationType, UserLevel] => !!value[0] && value[1] !== null),
    switchMap(([selectedLocationType, userAccessLevel]) => {
      const isPscSearch = selectedLocationType === SelectedLocationType.PscHome;

      return isPscSearch
        ? combineLatest([this.isPreAccessioningEnabled$, this.isAnalyticsEnabled$]).pipe(
            map(([isPreAccessioningEnabled, isAnalyticsEnabled]) => {
              return menuItemGroupsMap[selectedLocationType].map(menuItemGroup => {
                return {
                  ...menuItemGroup,
                  menuItems: this.filterMenuItems(
                    menuItemGroup.menuItems,
                    true,
                    true,
                    true,
                    isPreAccessioningEnabled,
                    isAnalyticsEnabled,
                    userAccessLevel
                  )
                };
              });
            })
          )
        : combineLatest([
            this.isSupplyOrderingEnabled$,
            this.isEmrEnabled$,
            this.isGlobalResultsSearchEnabled$,
            this.isPreAccessioningEnabled$,
            this.isAnalyticsEnabled$
          ]).pipe(
            map(([isSupplyOrderingEnabled, isEmrEnabled, isGlobalResultsSearchEnabled, isPreAccessioningEnabled, isAnalyticsEnabled]) => {
              const itemGroups = menuItemGroupsMap[selectedLocationType] || [];

              return itemGroups.map(menuItemGroup => {
                return {
                  ...menuItemGroup,
                  menuItems: this.filterMenuItems(
                    menuItemGroup.menuItems,
                    isSupplyOrderingEnabled,
                    isEmrEnabled,
                    isGlobalResultsSearchEnabled,
                    isPreAccessioningEnabled,
                    isAnalyticsEnabled,
                    userAccessLevel
                  )
                };
              });
            })
          );
    })
  );

  public readonly menuItemGroups$: Observable<SideBarMenuItemGroup[]> = combineLatest([this.isAdminMode$, this.userAccessLevel$]).pipe(
    filter((value): value is [boolean, UserLevel] => value[1] !== null),
    switchMap(([isAdminMode, userAccessLevel]) =>
      isAdminMode ? of(this.buildAdminMenuGroups(userAccessLevel)) : this.locationMenuItemGroups$
    )
  );

  private buildAdminMenuGroups(userAccessLevel: UserLevel): SideBarMenuItemGroup[] {
    return adminMenuItemsGroup.map(group => ({
      ...group,
      menuItems: this.filterMenuItems(group.menuItems, false, false, false, false, false, userAccessLevel)
    }));
  }

  private checkAccessLevel(menuItem: SideBarMenuItem, userAccessLevel: UserLevel): boolean {
    return !menuItem?.accessLevelGroup || accessLevelBelongToGroup(menuItem.accessLevelGroup, userAccessLevel);
  }

  private shouldMenuItemPresented(
    item: SideBarMenuItem,
    isSupplyOrderingEnabled: boolean | undefined = false,
    isEmrEnabled: boolean | undefined = false,
    isGlobalResultsSearchEnabled: boolean | undefined = false,
    isPreAccessioningEnabled: boolean = false,
    isAnalyticsEnabled: boolean = false,
    userAccessLevel: UserLevel
  ): boolean {
    switch (item?.feature) {
      case AppFeatures.SupplyOrders:
        return isSupplyOrderingEnabled;
      case AppFeatures.EmrOrders:
        return isEmrEnabled;
      case AppFeatures.GlobalResultsSearch:
      case AppFeatures.GlobalResultsReports:
        return isGlobalResultsSearchEnabled;
      case AppFeatures.PreaccessionManifests:
        return isPreAccessioningEnabled;
      case AppFeatures.Analytics:
        return isAnalyticsEnabled;
      case AppFeatures.OrderRequisitionBatch:
        return true;
      default:
        return this.checkAccessLevel(item, userAccessLevel);
    }
  }

  private filterMenuItems(
    menuItems: SideBarMenuItem[],
    isSupplyOrderingEnabled: boolean | undefined = false,
    isEmrEnabled: boolean | undefined = false,
    isGlobalResultsSearchEnabled: boolean | undefined = false,
    isPreAccessioningEnabled: boolean = false,
    isAnalyticsEnabled: boolean = false,
    userAccessLevel: UserLevel
  ): SideBarMenuItem[] {
    return menuItems.reduce<SideBarMenuItem[]>((acc, menuItem) => {
      const newMenuItem = { ...menuItem };

      if (newMenuItem.menuItems) {
        newMenuItem.menuItems = this.filterMenuItems(
          newMenuItem.menuItems,
          isSupplyOrderingEnabled,
          isEmrEnabled,
          isGlobalResultsSearchEnabled,
          isPreAccessioningEnabled,
          isAnalyticsEnabled,
          userAccessLevel
        );
      }
      const hideMenuIfSubItemsIsFiltered =
        menuItem.menuItems !== undefined ? menuItem.menuItems.length > 0 && newMenuItem.menuItems?.length === 0 : false;
      if (
        !hideMenuIfSubItemsIsFiltered &&
        this.checkAccessLevel(menuItem, userAccessLevel) &&
        this.shouldMenuItemPresented(
          newMenuItem,
          isSupplyOrderingEnabled,
          isEmrEnabled,
          isGlobalResultsSearchEnabled,
          isPreAccessioningEnabled,
          isAnalyticsEnabled,
          userAccessLevel
        )
      ) {
        acc.push(newMenuItem);
      }
      return acc;
    }, [] as SideBarMenuItem[]);
  }
}
